import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import colors from '../../colors';
import typography from '../../typography';
import spacingSystem from '../../spacing';

// Input sizes
export type InputSize = 'small' | 'medium' | 'large';

// Input variants
export type InputVariant = 'default' | 'filled' | 'outlined';

// Input props
export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * The size of the input
   * @default 'medium'
   */
  size?: InputSize;
  
  /**
   * The variant of the input
   * @default 'default'
   */
  variant?: InputVariant;
  
  /**
   * Whether the input is disabled
   * @default false
   */
  disabled?: boolean;
  
  /**
   * Whether the input is in an error state
   * @default false
   */
  error?: boolean;
  
  /**
   * Error message to display
   */
  errorMessage?: string;
  
  /**
   * Label for the input
   */
  label?: string;
  
  /**
   * Helper text to display below the input
   */
  helperText?: string;
  
  /**
   * Whether the input should take the full width of its container
   * @default false
   */
  fullWidth?: boolean;
  
  /**
   * Icon to display at the start of the input
   */
  startIcon?: React.ReactNode;
  
  /**
   * Icon to display at the end of the input
   */
  endIcon?: React.ReactNode;
  
  /**
   * Additional class name
   */
  className?: string;
}

// Size styles
const getSizeStyles = (size: InputSize) => {
  switch (size) {
    case 'small':
      return css`
        padding: ${spacingSystem.spacing[4]} ${spacingSystem.spacing[8]};
        font-size: ${typography.fontSize.sm};
        height: 32px;
      `;
    case 'medium':
      return css`
        padding: ${spacingSystem.spacing[8]} ${spacingSystem.spacing[12]};
        font-size: ${typography.fontSize.md};
        height: 40px;
      `;
    case 'large':
      return css`
        padding: ${spacingSystem.spacing[12]} ${spacingSystem.spacing[16]};
        font-size: ${typography.fontSize.lg};
        height: 48px;
      `;
    default:
      return '';
  }
};

// Variant styles
const getVariantStyles = (variant: InputVariant, error: boolean) => {
  const errorColor = colors.semantic.error.default;
  const borderColor = error ? errorColor : colors.neutral[300];
  const focusBorderColor = error ? errorColor : colors.primary[500];
  
  switch (variant) {
    case 'default':
      return css`
        background-color: white;
        border: 1px solid ${borderColor};
        
        &:hover:not(:disabled) {
          border-color: ${error ? errorColor : colors.neutral[400]};
        }
        
        &:focus {
          border-color: ${focusBorderColor};
          box-shadow: 0 0 0 3px ${error ? colors.semantic.error.light : colors.primary[100]};
        }
      `;
    case 'filled':
      return css`
        background-color: ${colors.neutral[100]};
        border: 1px solid transparent;
        
        &:hover:not(:disabled) {
          background-color: ${colors.neutral[200]};
        }
        
        &:focus {
          background-color: white;
          border-color: ${focusBorderColor};
          box-shadow: 0 0 0 3px ${error ? colors.semantic.error.light : colors.primary[100]};
        }
      `;
    case 'outlined':
      return css`
        background-color: transparent;
        border: 2px solid ${borderColor};
        
        &:hover:not(:disabled) {
          border-color: ${error ? errorColor : colors.neutral[400]};
        }
        
        &:focus {
          border-color: ${focusBorderColor};
          box-shadow: 0 0 0 2px ${error ? colors.semantic.error.light : colors.primary[100]};
        }
      `;
    default:
      return '';
  }
};

const InputContainer = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
`;

const Label = styled.label<{ error?: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.error ? colors.semantic.error.default : colors.neutral[800]};
  margin-bottom: ${spacingSystem.spacing[4]};
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

// Define the StyledInputProps type with transient props
interface StyledInputProps {
  $size?: InputSize;
  $variant?: InputVariant;
  $error?: boolean;
  $fullWidth?: boolean;
  $disabled?: boolean;
  $startIcon?: React.ReactNode;
  $endIcon?: React.ReactNode;
}

// Use the StyledInputProps type with attrs
const StyledInput = styled.input.attrs<StyledInputProps>(() => ({
  // Don't pass any custom props to the HTML element
}))`
  /* Base styles */
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.regular};
  color: ${colors.neutral[900]};
  border-radius: 4px;
  width: 100%;
  outline: none;
  transition: all 0.2s ease-in-out;
  
  /* Size styles */
  ${(props: StyledInputProps) => getSizeStyles(props.$size || 'medium')}
  
  /* Variant styles */
  ${(props: StyledInputProps) => getVariantStyles(props.$variant || 'default', Boolean(props.$error))}
  
  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: ${colors.neutral[100]};
    border-color: ${colors.neutral[200]};
  }
  
  /* Placeholder */
  &::placeholder {
    color: ${colors.neutral[400]};
  }
  
  /* Icon padding adjustments */
  ${props => props.$startIcon && css`
    padding-left: ${spacingSystem.spacing[32]};
  `}
  
  ${props => props.$endIcon && css`
    padding-right: ${spacingSystem.spacing[32]};
  `}
`;

const IconWrapper = styled.div<{ position: 'start' | 'end' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.position === 'start' ? 'left: 12px;' : 'right: 12px;'}
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.neutral[500]};
`;

const HelperText = styled.p<{ error?: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xs};
  color: ${props => props.error ? colors.semantic.error.default : colors.neutral[600]};
  margin-top: ${spacingSystem.spacing[4]};
  margin-bottom: 0;
`;

/**
 * Input component for text entry
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  size = 'medium',
  variant = 'default',
  disabled = false,
  error = false,
  errorMessage,
  label,
  helperText,
  fullWidth = false,
  startIcon,
  endIcon,
  className,
  ...rest
}, ref) => {
  // Generate a unique ID for the input
  const id = React.useId();
  
  return (
    <InputContainer fullWidth={fullWidth} className={className}>
      {label && (
        <Label htmlFor={id} error={error}>
          {label}
        </Label>
      )}
      
      <InputWrapper>
        {startIcon && (
          <IconWrapper position="start">
            {startIcon}
          </IconWrapper>
        )}
        
        <StyledInput
          id={id}
          ref={ref}
          // Use transient props with $ prefix for all custom props
          $size={size}
          $variant={variant}
          $error={error}
          $fullWidth={fullWidth}
          $disabled={disabled}
          $startIcon={startIcon}
          $endIcon={endIcon}
          // Pass HTML attributes directly
          disabled={disabled}
          aria-invalid={error}
          aria-describedby={errorMessage || helperText ? `${id}-helper-text` : undefined}
          {...rest}
        />
        
        {endIcon && (
          <IconWrapper position="end">
            {endIcon}
          </IconWrapper>
        )}
      </InputWrapper>
      
      {(errorMessage || helperText) && (
        <HelperText id={`${id}-helper-text`} error={error}>
          {error && errorMessage ? errorMessage : helperText}
        </HelperText>
      )}
    </InputContainer>
  );
});

Input.displayName = 'Input';

export default Input;